import { useTranslation } from 'react-i18next';
import { EMPTY_VALUE } from '../../constants/displayConstants';
import type {
  SimulationProgressRow,
  SimulationShortfallReason
} from '../../services/simulationProgressService';
import { formatNumberRange } from '../../utils/numberUtils';
import { Table, type TableColumn } from '../UI/Table';
import styles from './YearPlanSimulationModal.module.css';

interface SimulationProgressTableProps {
  rows: SimulationProgressRow[];
  // The reasons only settle once every course has been tried, so they are
  // held back until the run is done rather than shown changing under it.
  isDone: boolean;
  professionName: (professionId: string) => string;
}

const COUNT_COLUMN_WIDTH = 110;
const COUNT_CELL_STYLE: React.CSSProperties = { justifyContent: 'flex-end', textAlign: 'right' };

const REASON_KEYS: Record<SimulationShortfallReason['kind'], string> = {
  beds: 'simulation-reason-beds',
  staff: 'simulation-reason-staff',
  'too-long': 'simulation-reason-too-long',
  'without-weeks': 'simulation-reason-without-weeks',
  'no-room': 'simulation-reason-no-room'
};

export const SimulationProgressTable = ({
  rows,
  isDone,
  professionName
}: SimulationProgressTableProps) => {
  const { t } = useTranslation();

  const describeReason = (reason: SimulationShortfallReason): string => {
    if (reason.kind !== 'beds' && reason.kind !== 'staff') return t(REASON_KEYS[reason.kind]);
    return t(REASON_KEYS[reason.kind], {
      count: reason.toWeekNumber - reason.fromWeekNumber + 1,
      weeks: formatNumberRange(reason.fromWeekNumber, reason.toWeekNumber),
      profession: reason.kind === 'staff' ? professionName(reason.professionId) : ''
    });
  };

  const columns: TableColumn<SimulationProgressRow>[] = [
    {
      key: 'template',
      header: t('simulation-column-template'),
      render: (row) => (
        <div className={styles.progressName}>
          <span>{row.templateName}</span>
          {isDone &&
            row.reasons.map((reason) => (
              <span key={reason.kind === 'staff' ? reason.professionId : reason.kind} className={styles.reason}>
                {describeReason(reason)}
              </span>
            ))}
        </div>
      )
    },
    {
      key: 'placed',
      header: t('simulation-column-placed'),
      width: COUNT_COLUMN_WIDTH,
      headerAlign: 'right',
      cellStyle: COUNT_CELL_STYLE,
      render: (row) => (
        <span className={styles.count}>
          {t('simulation-placed-of', {
            placed: row.placedCourseCount,
            requested: row.requestedCourseCount
          })}
        </span>
      )
    },
    {
      key: 'unplaced',
      header: t('simulation-column-unplaced'),
      width: COUNT_COLUMN_WIDTH,
      headerAlign: 'right',
      cellStyle: COUNT_CELL_STYLE,
      render: (row) => (
        <span className={`${styles.count} ${row.unplacedCourseCount > 0 ? styles.countShort : ''}`}>
          {row.unplacedCourseCount > 0 ? row.unplacedCourseCount : EMPTY_VALUE}
        </span>
      )
    }
  ];

  return <Table columns={columns} data={rows} getRowKey={(row) => row.templateId} />;
};
